import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import api, { imageUrl, pdfUrl } from "@/lib/api";
import { embedUrl } from "@/lib/video";
import { Check, ArrowLeft, ArrowRight, Download, FileText } from "lucide-react";

export default function MachineDetail() {
  const { id } = useParams();
  const { t, machine, lang } = useLang();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/catalog/item/${id}`)
      .then(({ data }) => setItem(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex min-h-[50vh] items-center justify-center text-slate-500">{t("common.loading")}</div>;
  if (notFound || !item) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center" data-testid="machine-notfound">
        <p className="text-slate-600">{t("catalog.notFound")}</p>
        <Link to="/catalog" className="mt-4 inline-flex items-center gap-1 text-sm font-bold uppercase tracking-wider text-blue-700">
          <ArrowLeft className="h-4 w-4" /> {t("catalog.back")}
        </Link>
      </div>
    );
  }

  const tr = (item.translations && item.translations[lang]) || machine(item.model) || {};
  const name = tr.name || item.name;
  const description = tr.description || item.description;
  const features = (tr.features && tr.features.length) ? tr.features : (item.features || []);
  const pdf = pdfUrl(item.pdf_filename);
  const videos = item.video_urls || [];

  return (
    <div data-testid="machine-detail-page">
      <div className="mx-auto max-w-7xl px-4 pt-8 sm:px-6">
        <Link to="/catalog" className="inline-flex items-center gap-1 text-sm font-bold uppercase tracking-wider text-slate-500 hover:text-blue-700" data-testid="detail-back">
          <ArrowLeft className="h-4 w-4" /> {t("catalog.back")}
        </Link>
      </div>

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 py-8 sm:px-6 lg:grid-cols-2">
        <div className="overflow-hidden border border-slate-200 bg-slate-100">
          <img src={imageUrl(item.image_url)} alt={name} className="h-full w-full object-cover" data-testid="detail-image" />
        </div>

        <div>
          <span className="inline-block border border-slate-300 px-2 py-0.5 font-mono text-xs uppercase tracking-wider text-slate-500">{t(`categories.${item.category}`)}</span>
          <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl" data-testid="detail-name">{name}</h1>
          {item.model && <p className="mt-1 font-mono text-base text-blue-700" data-testid="detail-model">{item.model}</p>}
          <p className="mt-5 text-base leading-relaxed text-slate-700" data-testid="detail-description">{description}</p>

          {features.length > 0 && (
            <div className="mt-8">
              <h2 className="mb-3 text-sm font-bold uppercase tracking-wider text-slate-950">{t("catalog.featuresTitle")}</h2>
              <ul className="space-y-2">
                {features.map((f, i) => (
                  <li key={`${item.id}-f${i}`} className="flex items-start gap-2 text-sm text-slate-700">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-blue-700" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-10 flex flex-wrap gap-4">
            {pdf ? (
              <a href={pdf} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-none bg-blue-700 px-6 py-3 text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-blue-800" data-testid="detail-download-pdf">
                <Download className="h-4 w-4" /> {t("catalog.download")}
              </a>
            ) : (
              <span className="inline-flex items-center gap-2 border border-dashed border-slate-300 px-6 py-3 text-sm font-medium text-slate-400" data-testid="detail-no-pdf">
                <FileText className="h-4 w-4" /> {t("catalog.noPdf")}
              </span>
            )}
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-none border border-slate-900 px-6 py-3 text-sm font-bold uppercase tracking-widest text-slate-900 transition-colors hover:bg-slate-900 hover:text-white" data-testid="detail-contact">
              {t("common.request")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>

      {videos.length > 0 && (
        <div className="mx-auto max-w-7xl px-4 pb-16 sm:px-6" data-testid="detail-videos">
          <h2 className="mb-6 border-b-2 border-slate-900 pb-3 text-2xl font-bold tracking-tight text-slate-950">{t("catalog.videosTitle")}</h2>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {videos.map((v, i) => (
              <div key={`v${i}`} className="aspect-video overflow-hidden border border-slate-200 bg-slate-900" data-testid={`detail-video-${i}`}>
                <iframe
                  src={embedUrl(v)}
                  title={`${name} video ${i + 1}`}
                  className="h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
